import { useEffect, useRef, useState, useCallback } from "react";
import { MapPin, Loader2, Crosshair, X, Star } from "lucide-react";
import api from "@/lib/api";

/**
 * AddressAutocomplete — Google-Maps-style address picker for chauffeur flows.
 *
 * As the user types, debounces 300ms then merges TWO sources:
 *   1. Popular pins this platform has seen near `nearLat/nearLng` (or
 *      anywhere if no anchor passed)  — instant, no Nominatim hit.
 *   2. Live Nominatim worldwide search                                — slow,
 *      but always fresh.
 * Popular pins float above Nominatim hits (with a "★ N uses" badge) so
 * familiar places get one-tap selection.
 *
 * Selecting a result fills `value.address` AND emits `lat`/`lng` so the
 * parent map can recenter automatically. The pin is then POSTed to
 * `/api/chauffeur/locations/record` so future searches benefit from it.
 *
 * Props:
 *   value          { lat, lng, address }     — controlled state
 *   onChange       (next) => void            — called with new {lat,lng,address}
 *   placeholder    string                    — input hint
 *   label          string                    — displayed above input
 *   icon           ReactNode                 — defaults to <MapPin />
 *   showUseCurrent bool                      — show "Use my location" chip
 *   country        string                    — optional ISO-3166-1 alpha-2
 *                                              bias (e.g. inherited from
 *                                              the pickup's country)
 *   nearLat/nearLng number                   — anchor for popular-pin radius
 *   kind           "pickup"|"dropoff"        — recorded with the pin
 *   "data-testid"  string                    — root testid
 */
export default function AddressAutocomplete({
  value, onChange, placeholder, label, icon, showUseCurrent = false,
  country = null, nearLat = null, nearLng = null, kind = "pickup",
  "data-testid": tid = "address-autocomplete",
}) {
  const [text, setText] = useState(value?.address || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const lastQueryRef = useRef("");

  // Sync external value → input when parent updates (e.g. tap-on-map flow).
  useEffect(() => {
    setText(value?.address || "");
  }, [value?.address]);

  // Close dropdown on outside click.
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.trim().length < 3) {
      setResults([]); setOpen(false); return;
    }
    if (lastQueryRef.current === q) { setOpen(true); return; }
    lastQueryRef.current = q;
    setLoading(true);

    // Merge two sources in parallel:
    //   • popular pins this platform has seen (instant — no Nominatim hit)
    //   • live worldwide Nominatim search
    const popularParams = { q, limit: 4 };
    if (country) popularParams.country = country;
    if (Number.isFinite(nearLat) && Number.isFinite(nearLng)) {
      popularParams.lat = nearLat;
      popularParams.lng = nearLng;
      popularParams.radius_km = 50;
    }
    const liveParams = { q, limit: 6 };
    if (country) liveParams.country = country;

    try {
      const [popRes, liveRes] = await Promise.all([
        api.get("/chauffeur/locations/popular", { params: popularParams }).catch(() => ({ data: { results: [] } })),
        api.get("/chauffeur/geocode/search", { params: liveParams }).catch(() => ({ data: { results: [] } })),
      ]);
      const popular = (popRes.data?.results || []).map((r) => ({ ...r, _popular: true }));
      const live = liveRes.data?.results || [];
      // De-dupe live results that already appear in popular (rounded coords).
      const seen = new Set(popular.map((p) => `${(p.lat || 0).toFixed(4)},${(p.lng || 0).toFixed(4)}`));
      const dedupedLive = live.filter((r) => {
        const k = `${(r.lat || 0).toFixed(4)},${(r.lng || 0).toFixed(4)}`;
        if (seen.has(k)) return false;
        seen.add(k); return true;
      });
      const merged = [...popular, ...dedupedLive].slice(0, 8);
      setResults(merged);
      setOpen(merged.length > 0);
      setActiveIdx(-1);
    } catch (err) {
      setResults([]); setOpen(false);
    } finally {
      setLoading(false);
    }
  }, [country, nearLat, nearLng]);

  const onTyping = (q) => {
    setText(q);
    onChange({ ...(value || {}), address: q });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(q), 300);
  };

  const choose = (hit) => {
    const short = (hit.display_name || "").split(",").slice(0, 3).join(",").trim();
    onChange({ lat: hit.lat, lng: hit.lng, address: short || hit.display_name });
    setText(short || hit.display_name);
    setOpen(false);
    setResults([]);
    // Fire-and-forget: record this pin so future searches benefit. Don't
    // block the UI on it. Skip when the choice came from popular (the
    // count is already incremented backend-side via this same endpoint
    // — calling again still increments and that's fine, it just sharpens
    // ranking on hot pins).
    try {
      api.post("/chauffeur/locations/record", {
        lat: hit.lat, lng: hit.lng,
        display_name: hit.display_name || short || "",
        short_label: hit.short_label || short || "",
        country: hit.country || country || null,
        kind,
      }).catch(() => {/* silent */});
    } catch {/* silent */}
  };

  const useCurrent = () => {
    if (!navigator.geolocation) return;
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        const lat = p.coords.latitude;
        const lng = p.coords.longitude;
        // Reverse-geocode for a friendly label (best-effort)
        try {
          const r = await api.get(`/chauffeur/geocode/search`, {
            params: { q: `${lat.toFixed(5)},${lng.toFixed(5)}`, limit: 1 },
          });
          const first = r.data?.results?.[0];
          const label = first?.display_name?.split(",").slice(0, 3).join(",").trim() || "Current location";
          onChange({ lat, lng, address: label });
          setText(label);
        } catch {
          onChange({ lat, lng, address: "Current location" });
          setText("Current location");
        } finally { setLoading(false); }
      },
      () => setLoading(false),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const clear = () => {
    onChange({ lat: 0, lng: 0, address: "" });
    setText(""); setResults([]); setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      choose(results[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid={tid}>
      {label && (
        <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-1 inline-flex items-center gap-1">
          {icon ?? <MapPin size={12} />} {label}
        </label>
      )}
      <div className="relative">
        <input
          data-testid={`${tid}-input`}
          type="text"
          value={text}
          onChange={(e) => onTyping(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder || "Start typing an address…"}
          className="w-full bg-white/5 border border-white/15 hover:border-gold/30 focus:border-gold focus:ring-1 focus:ring-gold/30 rounded-md px-3 py-2 pr-9 text-sm text-white placeholder:text-white/40 transition-colors outline-none"
          autoComplete="off"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {loading && <Loader2 size={14} className="text-gold animate-spin" />}
          {!loading && text && (
            <button
              type="button" data-testid={`${tid}-clear`}
              onClick={clear}
              className="text-white/40 hover:text-white p-0.5"
              aria-label="Clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {showUseCurrent && (
        <button
          type="button" data-testid={`${tid}-use-current`}
          onClick={useCurrent}
          className="mt-1 inline-flex items-center gap-1 text-xs text-gold/80 hover:text-gold transition-colors"
        >
          <Crosshair size={12} /> Use my current location
        </button>
      )}

      {open && results.length > 0 && (
        <div
          data-testid={`${tid}-dropdown`}
          className="absolute z-50 mt-1 w-full bg-zinc-950 border border-gold/30 rounded-md shadow-2xl max-h-72 overflow-y-auto"
        >
          {results.map((r, i) => {
            const parts = (r.display_name || "").split(",");
            const head = parts[0]?.trim();
            const tail = parts.slice(1, 4).join(",").trim();
            return (
              <button
                key={`${r.lat}-${r.lng}-${i}`}
                type="button"
                data-testid={`${tid}-option-${i}`}
                onClick={() => choose(r)}
                onMouseEnter={() => setActiveIdx(i)}
                className={`w-full text-left px-3 py-2 transition-colors flex items-start gap-2 ${
                  activeIdx === i
                    ? "bg-gold/15 text-gold"
                    : r._popular
                      ? "text-amber-200 hover:bg-amber-500/10"
                      : "text-white/80 hover:bg-white/5"
                }`}
              >
                {r._popular ? (
                  <Star size={14} className="text-amber-300 mt-0.5 flex-shrink-0 fill-amber-300/50" />
                ) : (
                  <MapPin size={14} className="text-gold/70 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate flex items-center gap-2">
                    {head || "Unnamed"}
                    {r._popular && r.count > 1 && (
                      <span className="text-[10px] text-amber-300/80 border border-amber-500/30 rounded px-1.5 py-0.5">
                        {r.count}× used
                      </span>
                    )}
                  </div>
                  {tail && <div className="text-xs text-white/50 truncate">{tail}</div>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
